import React, { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import { getStatusStyle } from './calendarUtils';

// Horizontal Gantt-like Timeline view across the visible week.
export function CalendarTimelineView({ days, filteredShifts, staff, onShiftClick, groupBy }) {
  const rows = useMemo(() => {
    if (groupBy === 'participant') {
      const map = {};
      filteredShifts.forEach(s => {
        const key = s.participant_id || 'unassigned';
        if (!map[key]) map[key] = { id: key, label: s.participant_name || 'Unassigned', shifts: [] };
        map[key].shifts.push(s);
      });
      return Object.values(map);
    }
    // Default: staff
    return staff.map(s => {
      const staffKey = s.user_id || s.id;
      return {
        id: staffKey,
        label: s.full_name,
        shifts: filteredShifts.filter(sh => sh.assigned_staff?.some(a => a.id === staffKey)),
      };
    }).filter(r => r.shifts.length > 0);
  }, [filteredShifts, staff, groupBy]);

  return (
    <div>
      <div className="flex sticky top-0 z-20 bg-white border-b">
        <div className="w-48 flex-shrink-0 p-2 border-r font-medium text-xs text-slate-600">
          {groupBy === 'participant' ? 'Participant' : 'Staff'}
        </div>
        {days.map(day => (
          <div key={day.toISOString()} className={`flex-1 min-w-[100px] text-center py-1.5 border-r ${isToday(day) ? 'bg-blue-50' : ''}`}>
            <p className="text-[10px] text-muted-foreground">{format(day, 'EEE')}</p>
            <p className={`text-xs font-semibold ${isToday(day) ? 'text-blue-700' : ''}`}>{format(day, 'd')}</p>
          </div>
        ))}
      </div>
      {rows.map(row => (
        <div key={row.id} className="flex border-b hover:bg-slate-50/50">
          <div className="w-48 flex-shrink-0 p-2 border-r bg-white sticky left-0 z-10">
            <p className="text-xs font-medium truncate">{row.label}</p>
            <p className="text-[10px] text-muted-foreground">{row.shifts.length} shifts</p>
          </div>
          {days.map(day => {
            const dk = format(day, 'yyyy-MM-dd');
            const dayShifts = row.shifts.filter(s => s.date === dk);
            return (
              <div key={dk} className={`flex-1 min-w-[100px] p-0.5 border-r ${isToday(day) ? 'bg-blue-50/30' : ''}`}>
                {dayShifts.map(s => {
                  const style = getStatusStyle(s.status);
                  return (
                    <div key={s.id} className={`rounded px-1 py-0.5 mb-0.5 cursor-pointer hover:shadow ${style.bg} border`} onClick={() => onShiftClick(s)}>
                      <p className={`text-[9px] font-semibold ${style.text}`}>{s.start_time}–{s.end_time}</p>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
      {rows.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm">No shifts to display</div>}
    </div>
  );
}
